import { CampaignType_Enum as CampaignType, Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useTranslation } from '@frontend/i18n';
import { createCsvParser } from './create-csv-parser';

type EmailCsvRow = {
  firstname: string;
  lastname: string;
  email: string;
  preferredname?: string;
};

type TextCsvRow = {
  firstname: string;
  lastname: string;
  phone: string;
  preferredname?: string;
};

type RecipientsParseResult = {
  recipients: Recipient[];
  error: string;
};

export const useCsvRecipientsParse = (locationId: string, campaignId: string, campaignType: CampaignType) => {
  const { t } = useTranslation('bulk-messaging');
  const parseCsv = campaignType === CampaignType.EMAIL ? createCsvParser<EmailCsvRow>() : createCsvParser<TextCsvRow>();

  const formatRecipientsData = (data: EmailCsvRow[] | TextCsvRow[]): Recipient[] =>
    data.map((row) => ({
      campaignId,
      destination: campaignType === CampaignType.EMAIL ? (row as EmailCsvRow).email : (row as TextCsvRow).phone,
      firstName: row.firstname,
      lastName: row.lastname,
      locationId,
      preferredName: row.preferredname ?? row.firstname,
    }));

  const getErrorMessage = (recipients: Recipient[]) => {
    if (recipients.length === 0) return t('No contacts found in CSV file.');

    const invalidRecipients = [];
    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];
      if (!recipient.firstName || !recipient.lastName || !recipient.destination) {
        // Start counting rows at 2 because the first row is the CSV header and the index is 0-based
        invalidRecipients.push(i + 2);
      }
    }

    if (invalidRecipients.length > 0) {
      switch (invalidRecipients.length) {
        case 1:
          return t('There is an issue with row {{rowNumber}}. Please fix the issue and try again.', {
            rowNumber: invalidRecipients[0],
          });

        case 2:
          return t(
            'There are issues with rows {{firstRowNumber}} and {{secondRowNumber}}. Please fix the issues and try again.',
            { firstRowNumber: invalidRecipients[0], secondRowNumber: invalidRecipients[1] }
          );

        case 3:
          return t(
            'There are issues with rows {{firstRowNumber}}, {{secondRowNumber}}, and {{thirdRowNumber}}. Please fix the issues and try again.',
            {
              firstRowNumber: invalidRecipients[0],
              secondRowNumber: invalidRecipients[1],
              thirdRowNumber: invalidRecipients[2],
            }
          );

        case 4:
          return t(
            'There are issues with rows {{firstRowNumber}}, {{secondRowNumber}}, {{thirdRowNumber}}, and 1 more row. Please fix the issues and try again.',
            {
              firstRowNumber: invalidRecipients[0],
              secondRowNumber: invalidRecipients[1],
              thirdRowNumber: invalidRecipients[2],
            }
          );

        default:
          return t(
            'There are issues with rows {{firstRowNumber}}, {{secondRowNumber}}, {{thirdRowNumber}}, and {{additionalRowsCount}} more rows. Please fix the issues and try again.',
            {
              firstRowNumber: invalidRecipients[0],
              secondRowNumber: invalidRecipients[1],
              thirdRowNumber: invalidRecipients[2],
              additionalRowsCount: invalidRecipients.length - 3,
            }
          );
      }
    }

    return '';
  };

  const parseRecipientsFromCsv = async (files: File[]): Promise<RecipientsParseResult> => {
    try {
      if (!files[0]) return result(t('Please upload a CSV file.'));

      const results = await parseCsv(files);
      if (!results) return result(t('No contacts found in CSV file.'));

      const recipients = formatRecipientsData(results.data);
      const error = getErrorMessage(recipients);
      return {
        recipients,
        error,
      };
    } catch {
      return result(t('An unexpected error occurred while reading your CSV file.'));
    }
  };

  return parseRecipientsFromCsv;
};

const result = (error: string, recipients: Recipient[] = []): RecipientsParseResult => {
  return { recipients, error };
};
